<!--
  Administration page for one scholarship: what it is worth, how much is
  committed, who holds an award for the cycle, and every application it received.
-->
@if (error()) {
  <div class="alert alert-warning"><i class="fas fa-exclamation-circle"></i> {{ error() }}</div>
}
@if (message()) {
  <div class="alert alert-success"><i class="fas fa-check-circle"></i> {{ message() }}</div>
}

@if (!scholarshipId) {
  <div class="card">
    <div class="card-body">
      <div class="empty-state">
        <i class="fas fa-graduation-cap"></i>
        <h3>No scholarship selected</h3>
        <p>Choose a scholarship from Scholarship Management to administer its cycle.</p>
        <button type="button" class="btn btn-primary" (click)="requestBack()">
          <i class="fas fa-arrow-left"></i> Back to Scholarship Management
        </button>
      </div>
    </div>
  </div>
}

@if (scholarshipId) {
  <!-- ============ Scholarship and its cycle ============ -->
  <div class="card">
    <div class="card-header">Scholarship</div>
    <div class="card-body">
      <div class="cycle-header">
        <div class="cycle-identity">
          <h3 class="cycle-title">{{ scholarship()?.title || 'Scholarship' }}</h3>
          <p class="cycle-meta">
            <span [class]="cycleOpen ? 'status status-approved' : 'status status-cancelled'">
              {{ cycleStatusLabel }}
            </span>
            @if (scholarship()?.deadline) {
              <span class="cycle-figure">
                Deadline {{ scholarship()?.deadline | date: 'dd MMM yyyy' }}
                @if (daysToDeadline !== null && daysToDeadline >= 0) {
                  ({{ daysToDeadline }} day{{ daysToDeadline === 1 ? '' : 's' }} left)
                } @else if (daysToDeadline !== null) {
                  ({{ daysOverdue }} day{{ daysOverdue === 1 ? '' : 's' }} overdue)
                }
              </span>
            }
            @if (scholarship()?.allocationFrequency?.name) {
              <span class="cycle-figure">{{ scholarship()?.allocationFrequency?.name }}</span>
            }
          </p>
        </div>

        <button type="button" class="btn btn-secondary btn-sm" (click)="requestBack()">
          <i class="fas fa-arrow-left"></i> Back to scholarships
        </button>
      </div>

      <!-- Cycle selector: the budget, awards and claims are all cycle-scoped. -->
      <form class="toolbar" (ngSubmit)="applyCycle()">
        <label>
          Cycle
          <input type="text" name="cycle" [(ngModel)]="cycle" placeholder="e.g. 2026" />
        </label>

        <div class="toolbar-actions">
          <button type="submit" class="btn btn-primary btn-sm" [disabled]="loading()">
            <i class="fas fa-sync"></i> Apply
          </button>
        </div>
      </form>

      <h3 class="section-heading">Budget summary — cycle {{ cycle }}</h3>

      <div class="budget-summary">
        <div class="budget-figure">
          <span class="budget-label">Total allocated</span>
          <span class="budget-value">{{ money(totalAllocated, currency) }}</span>
        </div>
        <div class="budget-figure">
          <span class="budget-label">Allocation used</span>
          <span class="budget-value">{{ money(totalUsed, currency) }}</span>
        </div>
        <div class="budget-figure">
          <span class="budget-label">Remaining allocation</span>
          <span class="budget-value">{{ money(totalRemaining, currency) }}</span>
        </div>
        <div class="budget-figure">
          <span class="budget-label">Total claims</span>
          <span class="budget-value">{{ money(totalClaims, currency) }}</span>
        </div>
        <div class="budget-figure">
          <span class="budget-label">Awards</span>
          <span class="budget-value">{{ activeAwardCount }} active / {{ awardCount }} total</span>
        </div>
      </div>

      <div class="progress-track">
        <div class="progress-fill" [style.width]="usageWidth"></div>
      </div>
      <p class="progress-caption">
        {{ usagePercent }}% of the awarded allocation has been used.
        @if (allocatedFunds > 0) {
          The scholarship is advertised with {{ money(allocatedFunds, currency) }}.
        }
      </p>

      @if (claimReport(); as claims) {
        <div class="stat-grid">
          <div class="stat">
            <span class="stat-label">Claims logged</span>
            <span class="stat-value">{{ claims.submittedClaims }}</span>
          </div>
          <div class="stat">
            <span class="stat-label">Approved</span>
            <span class="stat-value">{{ claims.approvedClaims }}</span>
          </div>
          <div class="stat">
            <span class="stat-label">Rejected</span>
            <span class="stat-value">{{ claims.rejectedClaims }}</span>
          </div>
        </div>
      }
    </div>
  </div>

  <!-- ============ Students awarded this cycle ============ -->
  @if (authorization.hasPermission(permissions.AwardView)) {
    <div class="card">
      <div class="card-header">Awarded students — cycle {{ cycle }}</div>
      <div class="card-body">
        @if (awards().length === 0) {
          <div class="empty-state">
            <i class="fas fa-award"></i>
            <h3>No awards for this cycle</h3>
            <p>
              Approved applications are awarded automatically when they are approved. Use
              "Award this cycle" below to back-fill the cycle.
            </p>
          </div>
        }

        @if (awards().length > 0) {
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Award</th>
                  <th>Student</th>
                  <th>Status</th>
                  <th>Period</th>
                  <th>Allocated</th>
                  <th>Used</th>
                  <th>Remaining</th>
                </tr>
              </thead>
              <tbody>
                @for (award of awards(); track award.id) {
                  <tr>
                    <td class="row-title">{{ award.referenceNumber }}</td>
                    <td>
                      {{ award.student.fullName }}
                      @if (award.student.emailAddress) {
                        <small>{{ award.student.emailAddress }}</small>
                      }
                    </td>
                    <td><span [class]="awardStatusClass(award.status)">{{ award.status }}</span></td>
                    <td>
                      {{ award.startDate | date: 'dd MMM yyyy' }} – {{ award.endDate | date: 'dd MMM yyyy' }}
                      <small>{{ award.monthsRemaining }} month{{ award.monthsRemaining === 1 ? '' : 's' }} left</small>
                    </td>
                    <td>{{ money(award.totalAllocation, award.currency) }}</td>
                    <td>{{ money(award.usedAmount, award.currency) }}</td>
                    <td>{{ money(award.remainingAmount, award.currency) }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div>
    </div>
  }

  <!-- ============ Every application for this scholarship ============ -->
  @if (authorization.hasPermission(permissions.ApplicationReview)) {
    <div class="card">
      <div class="card-header">Applications <span class="count-pill">{{ filteredApplications.length }}</span></div>
      <div class="card-body">
        <form class="toolbar" (ngSubmit)="onFilterChange()">
          <label>
            Status
            <select name="statusFilter" [(ngModel)]="statusFilter" (ngModelChange)="onFilterChange()">
              <option value="ALL">All statuses</option>
              @for (status of applicationStatuses; track status) {
                <option [value]="status">{{ status }}</option>
              }
            </select>
          </label>

          <label>
            Search
            <input type="text" name="searchTerm" [(ngModel)]="searchTerm"
              (ngModelChange)="onFilterChange()" placeholder="Reference, applicant or email" />
          </label>

          <label>
            Rows
            <select name="pageSize" [(ngModel)]="pageSize" (ngModelChange)="onFilterChange()">
              @for (size of pageSizeOptions; track size) {
                <option [ngValue]="size">{{ size }}</option>
              }
            </select>
          </label>
        </form>

        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Applicant</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Last changed</th>
              </tr>
            </thead>
            <tbody>
              @if (loading()) {
                <tr><td colspan="5" class="empty-cell">Loading applications…</td></tr>
              }
              @if (!loading() && filteredApplications.length === 0) {
                <tr>
                  <td colspan="5" class="empty-cell">
                    No applications match these filters.
                  </td>
                </tr>
              }
              @for (application of paginatedApplications; track application.id) {
                <tr>
                  <td class="row-title">{{ application.referenceNumber }}</td>
                  <td>
                    {{ applicantName(application) }}
                    @if (application.user?.emailAddress) {
                      <small>{{ application.user?.emailAddress }}</small>
                    }
                  </td>
                  <td>{{ application.applicationDate | date: 'dd MMM yyyy' }}</td>
                  <td>
                    <span [class]="statusClass(application.applicationStatus?.name)">
                      {{ application.applicationStatus?.name || 'Unknown' }}
                    </span>
                  </td>
                  <td>
                    @if (application.statusChangedDate) {
                      {{ application.statusChangedDate | date: 'dd MMM yyyy' }}
                    } @else {
                      —
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        @if (filteredApplications.length > pageSize) {
          <app-pagination [currentPage]="currentPage" [totalPages]="totalPages"
            [totalItems]="filteredApplications.length" [showingFrom]="showingFrom" [showingTo]="showingTo"
            itemLabel="applications" (pageChange)="goToPage($event)">
          </app-pagination>
        }
      </div>
    </div>
  }

  <!-- ============ Award the cycle (scoped to this scholarship) ============ -->
  @if (authorization.hasPermission(permissions.AwardManage)) {
    <div class="card">
      <div class="card-header">Award this cycle</div>
      <div class="card-body">
        <div class="action-panel">
          <strong>Award every approved application for {{ grantCycleName }}</strong>
          <p class="field-hint">
            Approved applications are awarded automatically when they are approved. Use this to
            back-fill this scholarship's cycle, or preview what would be created. Running it twice
            is safe — already awarded applications are skipped.
          </p>

          <form class="inline-form" (ngSubmit)="grantCycle(false)">
            <label>
              Cycle
              <input type="text" name="grantCycle" [(ngModel)]="grantCycleName" />
            </label>
            <label>
              Starts
              <input type="date" name="startDate" [(ngModel)]="startDate" />
            </label>
            <label>
              Ends
              <input type="date" name="endDate" [(ngModel)]="endDate" />
            </label>

            <button type="button" class="btn btn-secondary btn-sm" (click)="grantCycle(true)" [disabled]="busy()">
              Preview
            </button>
            <button type="submit" class="btn btn-primary btn-sm" [disabled]="busy()">
              {{ busy() ? 'Working…' : 'Award cycle' }}
            </button>
          </form>

          @if (cycleResult(); as result) {
            <div class="result-note">
              <span>
                Considered {{ result.considered }} · awarded {{ result.awarded }} · skipped {{ result.skipped }}
                @if (result.dryRun) { (preview only) }
              </span>
              @for (skip of result.skips; track skip.scholarshipApplicationId) {
                <small>
                  {{ skip.applicationReferenceNumber || skip.scholarshipApplicationId }}
                  @if (skip.studentName) { — {{ skip.studentName }} }: {{ skip.reason }}
                </small>
              }
            </div>
          }
        </div>
      </div>
    </div>
  }
}
